<template>
  <div v-if="value" class="modal-overlay">
    <div class="modal-content" @click.stop>
      <v-card>
        <div class="modal-header">
          <h3>{{ $t('homepage.signInModal.title') }}</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <v-card-text class="modal-body">
          <form @submit.prevent="submit">
            <div>
              <v-alert :value="error" type="error" outlined>{{ error }}</v-alert>
                <div class="form-group">
                  <label for="email">{{ $t('homepage.signInModal.email') }}</label>
                  <input
                    id="email"
                    v-validate="'email'"
                    v-model="form.email"
                    type="email"
                    name="email"
                    required
                    :class="{ 'error': errors.first('email') }"
                  />
                  <span v-if="errors.first('email')" class="error-message">{{ errors.first('email') }}</span>
                </div>
                <div class="form-group">
                  <label for="password">{{ $t('homepage.signInModal.password') }}</label>
                  <div class="input-wrapper">
                    <input
                      id="password"
                      v-model="form.password"
                      :type="passwordFieldType"
                      name="password"
                      required
                      :class="{ 'error': errors.first('password') }"
                    />
                    <div class="password-toggle-area" @click="togglePasswordVisibility">
                      <v-icon class="password-toggle-icon">
                        {{ showPassword ? '$vuetify.icons.eye' : '$vuetify.icons.eye_slash' }}
                      </v-icon>
                    </div>
                  </div>
                  <span v-if="errors.first('password')" class="error-message">{{ errors.first('password') }}</span>
                </div>
              <v-card-actions>
                <v-checkbox v-model="staySignedIn" :label="$t('homepage.signInModal.staySignedIn')" />
                <v-spacer />
                <v-btn type="submit" color="primary" :disabled="submitting">{{ $t('homepage.signInModal.submitButton') }}</v-btn>
              </v-card-actions>
            </div>
          </form>
          <div class="form-actions">
            <v-btn color="primary" text :to="{ name: 'auth-forgot-password' }">{{ $t('homepage.signInModal.forgotPassword') }}</v-btn>
            <div class="button-divider"></div>
            <v-btn color="primary" text :to="{ path: '/register/email-confirmation/resend' }">{{ $t('homepage.signInModal.resendConfirmation') }}</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import store from '@/store'

export default Vue.extend({
  name: 'SignInModal',
  props: {
    value: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      form: {
        email: '',
        password: '',
      },
      showPassword: false,
      error: null,
      submitting: false,
      staySignedIn: true
    }
  },
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    async submit() {
      const isValid = await this.$validator.validateAll();
      if (isValid && !this.submitting) {
        try {
          this.submitting = true;
          this.error = null;
          await store.dispatch('authentication/signIn', {
            email: this.form.email,
            password: this.form.password,
            staySignedIn: this.staySignedIn
          });

          this.$router.push({ name: 'dashboard' });
          this.$emit('input', false)
        } catch (err: unknown) {
          const status = err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined;
          const msg = status === 401
            ? this.$t('homepage.signInModal.errorInvalid')
            : this.$t('homepage.signInModal.errorGeneric');
          this.error = msg;
        }

        this.submitting = false;
      }
    },
    closeModal() {
      this.form.email = ''
      this.form.password = ''
      this.$emit('input', false)
    },
  },
  computed: {
    passwordFieldType(): string {
      return this.showPassword ? 'text' : 'password';
    },
  },
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  animation: fadeIn 0.2s ease-out;
}

.modal-content {
  background: #fefefe;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;
}

.modal-header {
  background-color: #1876D1;
  color: white;
  padding: .75rem;
  border-radius: 10px 10px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.155rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.close-btn {
  color: white;
  font-size: 1.54rem;
  cursor: pointer;
  padding: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.modal-body {
  padding: 2rem;
}

.form-group {
  flex: 1;
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: #1f2937;
  font-size: 0.70rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  background: #f9fafb;
  color: #1f2937;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.form-group .input-wrapper input {
  padding-right: 3.5rem !important;
}

.password-toggle-area {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.password-toggle-icon {
  color: #666 !important;
  font-size: 20px !important;
}

.form-group input:focus {
  outline: none;
  border-color: #1876D1;
  background: #d1d5db;
  box-shadow: 0 0 0 3px rgba(24, 118, 209, 0.1);
}

.form-group input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.form-group input.error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.input-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #666;
  font-size: 20px;
}

.password-toggle:hover {
  color: #1876D1;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  font-weight: 500;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.button-divider {
  width: 1px;
  height: 30px;
  background-color: #d1d5db;
  margin: 0 0.5rem;
}

.btn {
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  min-width: 120px;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn:hover::before {
  left: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #1876D1, #1876D1);
  color: white;
  box-shadow: 0 4px 15px rgba(30, 64, 175, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1876D1, #1876D1);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(30, 64, 175, 0.4);
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    margin: 1rem;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .button-divider {
    display: none;
  }
  
  .btn {
    width: 100%;
  }
}

.recaptcha-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.vue-recaptcha {
  transform: scale(0.8);
  transform-origin: right center;
}
</style>